import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Printer, CheckCircle2, Clock, XCircle, Wallet, Receipt as ReceiptIcon, Hash } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_META: Record<string, { variant: any; icon: any; label: string }> = {
  paid: { variant: 'default', icon: CheckCircle2, label: 'Paid' },
  pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
  awaiting_verification: { variant: 'secondary', icon: Clock, label: 'Awaiting verification' },
  failed: { variant: 'destructive', icon: XCircle, label: 'Failed' },
  cancelled: { variant: 'outline', icon: XCircle, label: 'Cancelled' },
  refunded: { variant: 'outline', icon: CheckCircle2, label: 'Refunded' },
};

export default function WalletReceipt() {
  const { txId, refId } = useParams<{ txId?: string; refId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Two URL shapes:
  //   /dashboard/wallet/receipt/:txId            — by wallet_transactions.id
  //   /dashboard/wallet/receipt/sub/:refId       — by subscription_id (looked up via invoice)
  const lookupTxId = txId || null;
  const lookupSubId = refId || null;

  const { data: tx, isLoading } = useQuery({
    queryKey: ['wallet-receipt', lookupTxId, lookupSubId, user?.id],
    enabled: !!user && (!!lookupTxId || !!lookupSubId),
    queryFn: async () => {
      if (lookupTxId) {
        const { data } = await supabase
          .from('wallet_transactions' as any)
          .select('*').eq('id', lookupTxId).maybeSingle();
        return data as any;
      }
      // Subscription → invoice → tx via reference
      const { data: inv } = await supabase
        .from('payment_invoices' as any)
        .select('*').eq('subscription_id', lookupSubId)
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (!inv) return null;
      const { data: txn } = await supabase
        .from('wallet_transactions' as any)
        .select('*')
        .eq('reference_type', 'advertising_package')
        .eq('reference_id', (inv as any).package_id)
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      return (txn as any) || null;
    },
  });

  // Linked invoice for package_purchase / credit_purchase
  const { data: invoice } = useQuery({
    queryKey: ['wallet-receipt-invoice', tx?.id],
    enabled: !!tx && (tx.kind === 'package_purchase' || tx.kind === 'credit_purchase'),
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_invoices' as any)
        .select('*, advertising_packages(name, sku, credit_grants, duration_days)')
        .eq('user_id', user!.id)
        .eq('package_id', tx.reference_id)
        .order('created_at', { ascending: false }).limit(1).maybeSingle();
      return data as any;
    },
  });

  // Linked top-up request
  const { data: topup } = useQuery({
    queryKey: ['wallet-receipt-topup', tx?.id],
    enabled: !!tx && tx.reference_type === 'wallet_topup_request',
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_topup_requests' as any)
        .select('*').eq('id', tx.reference_id).maybeSingle();
      return data as any;
    },
  });

  if (isLoading) return <Skeleton className="h-96 max-w-2xl" />;
  if (!tx) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="p-8 text-center space-y-3">
          <p className="text-muted-foreground">Receipt not found.</p>
          <Button onClick={() => navigate('/dashboard/wallet')}>Back to Wallet</Button>
        </CardContent>
      </Card>
    );
  }

  const isCredit = tx.direction === 'credit';
  const statusKey = invoice?.status || (topup?.status === 'approved' ? 'paid' : topup?.status) || 'paid';
  const meta = STATUS_META[statusKey] || STATUS_META.paid;
  const StatusIcon = meta.icon;

  const credits = invoice?.advertising_packages?.credit_grants
    ? Object.entries(invoice.advertising_packages.credit_grants as Record<string, number>)
    : [];

  return (
    <div className="space-y-4 max-w-2xl print:max-w-full">
      <div className="flex items-center justify-between gap-2 print:hidden">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard/wallet"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Wallet</Link>
        </Button>
        <Button size="sm" variant="outline" onClick={() => window.print()} className="gap-1">
          <Printer className="h-3.5 w-3.5" /> Print / Save PDF
        </Button>
      </div>

      <Card className="print:shadow-none print:border-0">
        <CardHeader className="space-y-3 border-b">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Wallet Receipt</p>
              <CardTitle className="text-xl flex items-center gap-2 mt-1">
                <ReceiptIcon className="h-5 w-5 text-primary" />
                {isCredit ? 'Top-Up Confirmation' : tx.kind === 'package_purchase' ? 'Package Purchase' : tx.kind === 'credit_purchase' ? 'Credit Store Purchase' : 'Wallet Charge'}
              </CardTitle>
            </div>
            <Badge variant={meta.variant} className="gap-1">
              <StatusIcon className="h-3 w-3" /> {meta.label}
            </Badge>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-bold tabular-nums ${isCredit ? 'text-emerald-600' : 'text-destructive'}`}>
              {isCredit ? '+' : '-'}{Number(tx.amount_etb).toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground">ETB</span>
          </div>
        </CardHeader>

        <CardContent className="p-6 space-y-4 text-sm">
          {/* Reference IDs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Transaction ID" value={tx.id} mono />
            <Field label="Date" value={format(new Date(tx.created_at), 'd MMM yyyy, HH:mm')} />
            <Field label="Type" value={tx.kind.replace('_', ' ')} className="capitalize" />
            <Field label="Wallet balance after" value={`${Number(tx.balance_after).toLocaleString()} ETB`} />
            {tx.note && <Field label="Note" value={tx.note} className="sm:col-span-2" />}
          </div>

          {/* Top-up details */}
          {topup && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Top-Up Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Method" value={topup.method.replace('_', ' ')} className="capitalize" />
                {topup.bank_name && <Field label="Bank" value={topup.bank_name} />}
                {topup.payment_reference && <Field label="Payment ref." value={topup.payment_reference} mono />}
                <Field label="Request ID" value={topup.id} mono />
              </div>
            </div>
          )}

          {/* Invoice details (package / credit purchase) */}
          {invoice && (
            <div className="border-t pt-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Invoice</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Invoice no." value={invoice.invoice_number} mono />
                <Field label="Item" value={invoice.advertising_packages?.name || '—'} />
                {invoice.advertising_packages?.sku && (
                  <Field label="SKU" value={invoice.advertising_packages.sku} mono />
                )}
                {invoice.advertising_packages?.duration_days && (
                  <Field label="Validity" value={`${invoice.advertising_packages.duration_days} days`} />
                )}
                {invoice.paid_at && (
                  <Field label="Paid at" value={format(new Date(invoice.paid_at), 'd MMM yyyy, HH:mm')} />
                )}
                <Field label="Provider" value="Wallet (ETB)" />
              </div>

              {credits.length > 0 && (
                <div className="rounded-lg bg-muted/40 p-3 mt-2">
                  <p className="text-xs font-medium mb-2">Credits Applied</p>
                  <div className="grid grid-cols-2 gap-2">
                    {credits.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <span className="capitalize text-muted-foreground">{k.replace('_', ' ')}</span>
                        <span className="font-semibold">+{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer summary */}
          <div className="border-t pt-4 flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Wallet className="h-3 w-3" /> Paid from wallet
            </div>
            <div className="flex items-center gap-1">
              <Hash className="h-3 w-3" /> {tx.id.slice(0, 8).toUpperCase()}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, mono, className }: { label: string; value: string; mono?: boolean; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-sm break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  );
}
